import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Mail, Lock, ArrowRight, CheckCircle2 } from 'lucide-react'
import { useAuth } from '../../store/authStore'
import { checkUsername, rememberPassword } from '../../lib/api/auth'
import { PageShell } from '../../components/layout/PageShell'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function AuthScreen() {
  const { t } = useTranslation()
  const { locale = 'it' } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const status = useAuth((s) => s.status)
  const signIn = useAuth((s) => s.signIn)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<'unknown' | 'login' | 'register'>('unknown')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)

  const redirect = params.get('redirect') || `/${locale}/bookings`

  // already signed in → leave the auth screen
  if (status === 'authenticated') return <Navigate to={redirect} replace />

  const emailValid = EMAIL_RE.test(email)

  async function onEmailBlur() {
    setMode('unknown')
    if (!emailValid) return
    const exists = await checkUsername(email)
    setMode(exists ? 'login' : 'register')
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!emailValid || password.length < 6) {
      setError(t('auth.invalidInput'))
      return
    }
    setSubmitting(true)
    try {
      const res = await signIn(email.trim(), password)
      if (res.ok) navigate(redirect, { replace: true })
      else setError(t('auth.invalid'))
    } catch {
      setError(t('auth.invalid'))
    } finally {
      setSubmitting(false)
    }
  }

  async function onForgot() {
    if (!emailValid) {
      setError(t('auth.emailFirst'))
      return
    }
    await rememberPassword(email.trim())
    setResetSent(true)
    setError(null)
  }

  const submitLabel =
    mode === 'login' ? t('auth.login') : mode === 'register' ? t('auth.register') : t('auth.continue')

  return (
    <PageShell narrow>
      <div className="gsm-card p-6 sm:p-8">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900">{t('auth.title')}</h1>
        <p className="mt-1.5 text-sm text-ink-500">{t('auth.subtitle')}</p>

        {mode !== 'unknown' && (
          <p className="mt-4 rounded-xl bg-ink-50 px-3.5 py-2.5 text-[13px] text-ink-500">
            {mode === 'login' ? t('auth.hintLogin') : t('auth.hintRegister')}
          </p>
        )}

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <Field
            label={t('auth.email')}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="nome@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setResetSent(false)
            }}
            onBlur={onEmailBlur}
            rightSlot={<Mail className="mr-1.5 h-4 w-4 text-ink-300" />}
          />
          <Field
            label={t('auth.password')}
            type="password"
            name="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            placeholder={t('auth.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            rightSlot={<Lock className="mr-1.5 h-4 w-4 text-ink-300" />}
            error={error ?? undefined}
          />

          {resetSent && (
            <p className="flex items-center gap-2 rounded-xl bg-green-50 px-3.5 py-2.5 text-[13px] font-medium text-green-700">
              <CheckCircle2 className="h-4 w-4" /> {t('auth.resetSent')}
            </p>
          )}

          <Button type="submit" size="lg" className="w-full" loading={submitting} rightIcon={<ArrowRight className="h-4 w-4" />}>
            {submitLabel}
          </Button>
        </form>

        <button onClick={onForgot} className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-800">
          {t('auth.forgot')}
        </button>
      </div>

      <p className="mt-5 px-2 text-center text-xs text-ink-400">{t('auth.disclaimer')}</p>
    </PageShell>
  )
}
